<template>
  <v-card class="step-tab-panel" flat>
    <v-container class="cann-groups-container d-flex flex-column pa-0 ma-0">
      <v-row class="header ma-0 flex-grow-0">
        <v-col class="flex-grow-0 header__title">
          <!-- Page title -->
          <span class="font-weight-bold mr-1">{{ $t('scenarioCannGroups.cannGroupsSetup') }}</span>

          <!-- Cann groups tooltip -->
          <DocsLink link="toolguide/060-canngroups.html" />
        </v-col>

        <v-col
          v-if="showTreeStructureTrimmedWarning"
          cols="auto"
          class="flex-grow-0 pa-0 ml-2 mr-3 align-self-center"
        >
          <v-alert class="alert--small mb-0" type="info" variant="tonal">
            {{ $t('scenarioCannGroups.treeStructureTrimmedWarning') }}
          </v-alert>
        </v-col>
      </v-row>

      <div class="container-block flex-grow-1">
        <div class="tree ma-0">
          <div class="title-container">
            <div class="d-flex align-center">
              <div
                class="attribute-value attribute-value--all-products d-flex align-center float-left"
              >
                <p>{{ $t('scenarioCannGroups.allProducts') }}</p>
              </div>
            </div>
          </div>

          <div class="treeview-container">
            <v-treeview
              v-if="treeData"
              density="compact"
              item-value="key"
              :items="treeDataArray"
              :opened="openedNodes"
              :open-all="expandAll"
              indent-lines
              hide-actions
              @update:opened="setOpenNodes"
            >
              <template #prepend="{ item: node }">
                <!-- Chips group: provides position context so the bracket always sits beside the chips -->
                <div class="node-chips-group">
                  <!-- Bracket shown only on merged nodes (>1 attributeValue) -->
                  <div v-if="node.attributeValues.length > 1" class="node-group-line" />

                  <!-- Main holder for attribute value(s) -->
                  <div class="attributes-box d-flex flex-column">
                    <draggable
                      :list="getList(node)"
                      :item-key="element => element"
                      :options="{ disabled: isEditingDisabled }"
                      ghost-class="ghost"
                      :group="node.attributeId"
                      handle=".drag-handle"
                      @change="mergeGroup($event, node)"
                    >
                      <template #item="{ element }">
                        <div
                          class="attribute-value d-flex align-center justify-space-between mb-1"
                          :class="{
                            hidden: node.attributeValues.length < 1,
                            draggable: !isEditingDisabled,
                            'drag-disabled': isEditingDisabled,
                          }"
                        >
                          <p>
                            <v-tooltip location="top">
                              <template #activator="{ props }">
                                <span v-bind="props">
                                  {{ element || $t('scenarioCannGroups.blankAttributeValue') }}
                                </span>
                              </template>
                              {{ element || $t('scenarioCannGroups.blankAttributeValue') }}
                            </v-tooltip>
                          </p>
                          <!-- Div here to put icons together -->
                          <div class="mr-1">
                            <v-icon
                              v-if="node.attributeValues.length > 1"
                              :disabled="isEditingDisabled"
                              size="14"
                              @click="splitGroup(node, element)"
                            >
                              mdi-call-split
                            </v-icon>
                            <v-icon class="drag-handle" size="10"> $cross-move </v-icon>
                          </div>
                        </div>
                      </template>
                    </draggable>
                  </div>
                </div>
                <!-- end node-chips-group -->

                <!-- Product count and percentage box -->
                <v-tooltip
                  :disabled="!displayCannGroupHighlighting(node.products.length, node.children)"
                  content-class="rtls-tooltip"
                  max-width="160px"
                  location="bottom"
                >
                  <template #activator="{ props }">
                    <span
                      v-bind="props"
                      class="attribute-products align-self-start mb-1"
                      :class="{
                        'attribute-products-highlighted': displayCannGroupHighlighting(
                          node.products.length,
                          node.children
                        ),
                      }"
                    >
                      <b class="mr-1 ml-1">{{ node.products.length }}</b>
                      {{ formatSalesRatio(node, treeData) }}%
                    </span>
                  </template>
                  <span>
                    {{
                      $t('scenarioCannGroups.productSizeWarning', [
                        getCannGroupsRecommendedSizes.min,
                        getCannGroupsRecommendedSizes.max,
                      ])
                    }}
                  </span>
                </v-tooltip>

                <!-- Drop down for attribute splitter -->
                <v-select
                  :disabled="isEditingDisabled"
                  :class="{ 'attribute-not-split-select': !getValue(node.children) }"
                  class="treeview-select select mb-1"
                  item-title="name"
                  item-value="id"
                  hide-details
                  single-line
                  :items="getAttributesToSelect(node.key)"
                  :model-value="getValue(node.children)"
                  :placeholder="$t('scenarioCannGroups.selectAttribute')"
                  :menu-props="{ bottom: true, offsetY: true }"
                  @update:model-value="modifyTree($event, node)"
                >
                  <template #selection="{ item }">
                    <v-tooltip location="top">
                      <template #activator="{ props }">
                        <span v-bind="props">
                          {{ item.title }}
                        </span>
                      </template>
                      {{ item.title }}
                    </v-tooltip>
                  </template>
                </v-select>

                <!-- Delete button -->
                <v-btn
                  v-if="hasNodes(node.children)"
                  :disabled="isEditingDisabled"
                  class="align-self-start mb-1"
                  icon
                  variant="text"
                  @click="deleteNode(node)"
                >
                  <v-icon size="x-large">$trash</v-icon>
                </v-btn>
              </template>
            </v-treeview>
          </div>

          <!-- Loading overlay -->
          <v-overlay :model-value="isTreeLoading" :absolute="true" :opacity="0.1">
            <v-progress-circular indeterminate size="32" />
          </v-overlay>
        </div>

        <Sidepanel
          v-if="showSidepanel"
          :title="$t('cannGroupsSidepanel.title')"
          :toggle-btn-text="$t('cannGroupsSidepanel.toggleBtn')"
        >
          <v-form ref="leafNodeForm" v-model="valid" autocomplete="off" @submit.prevent>
            <draggable
              v-model="orderedLeafNodes"
              :disabled="isEditingDisabled"
              item-key="key"
              class="list-group"
              ghost-class="ghost"
              handle=".draggable__icon"
              @change="setCustomLeafNodesOrder"
            >
              <template #item="{ element }">
                <div class="list-group__item" :class="{ 'drag-disabled': isEditingDisabled }">
                  <v-icon :class="{ draggable__icon: !isEditingDisabled }" size="12">
                    $cross-move
                  </v-icon>
                  <v-tooltip location="left" :disabled="isEditing || !element.pathName">
                    <template #activator="{ props }">
                      <v-text-field
                        v-bind="props"
                        v-model.trim="element.name"
                        class="list-group__input rtls-text-field"
                        single-line
                        hide-details="auto"
                        :rules="rules"
                        :disabled="!element.pathName || isEditingDisabled"
                        @update:model-value="revalidateForm"
                        @focus="highlightInputText"
                        @blur="toggleEditing"
                        @keyup.enter="$event.target.blur()"
                      />
                    </template>
                    <span>{{ element.pathName }}</span>
                  </v-tooltip>
                  <CannGroupNote :leaf-node="element" />
                </div>
              </template>
            </draggable>
          </v-form>
        </Sidepanel>
      </div>

      <div
        data-id-e2e="btnsCannGroups"
        class="page-actions-container d-flex flex-end flex-row"
        style="justify-content: flex-end"
      >
        <PageActions
          :has-data-changes="hasDataChanges || hasSingleCannGroup"
          :has-data-errors="hasDataErrors"
          :save-disabled="isEditingDisabled"
          :is-discard-enabled="!isEditingDisabled"
          @discard="discard"
          @save="initCanngroupSave"
        />
        <ErrorTriangle
          :errors="{
            [$t('scenarioCannGroups.errorMessages.cannGroupMinSize')]: cannGroupMinimumSizeNotMet,
          }"
          :arrow="false"
          :tooltip-positioning="{ top: true }"
          style="padding-right: 2rem"
        />
      </div>

      <DependencyTreeFeedbackModal
        :value="dependencyTreeModalOpen"
        :results="dependencyTreeFeedback"
        page="cannGroups"
        @close="closeDependencyTreeModal"
        @commit="saveCannGroups(true)"
      />

      <UnsavedDataModal
        ref="unsavedDataModal"
        :value="isUnsavedDataModalOpen"
        @cancel="closeUnsavedDataModal"
        @confirm="confirmUnsavedDataModal"
      />

      <!-- confirmation dialog -->
      <v-dialog v-model="isConfirmWithoutChildrenModalOpen" :max-width="300">
        <BaseDialogCard
          :title="$t('scenarioCannGroups.dialogTitle')"
          @close="toggleIsConfirmWithoutChildrenModalOpen(false)"
        >
          <span>{{ $t('scenarioCannGroups.dialogMessage') }}</span>
          <template #footer>
            <v-row>
              <v-col class="d-flex justify-end">
                <v-btn
                  :disabled="isEditingDisabled"
                  action
                  @click="[saveCannGroups(), toggleIsConfirmWithoutChildrenModalOpen(false)]"
                >
                  {{ $t('actions.ok') }}
                </v-btn>
                <v-btn
                  :disabled="isEditingDisabled"
                  class="ml-2"
                  @click="toggleIsConfirmWithoutChildrenModalOpen(false)"
                >
                  {{ $t('actions.cancel') }}
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </BaseDialogCard>
      </v-dialog>
    </v-container>
  </v-card>
</template>

<script>
import {
  cloneDeep,
  difference,
  differenceWith,
  each,
  every,
  first,
  get,
  isArray,
  isEmpty,
  isEqual,
  isNull,
  isUndefined,
  keyBy,
  map,
  orderBy,
  partition,
  pull,
  set,
  size,
  sortBy,
  sortedIndexBy,
  sumBy,
  uniq,
} from 'lodash';
import { getCurrentInstance } from 'vue';
import { mapActions, mapState } from 'pinia';
import { v1 as uuidv1 } from 'uuid';

// composables
import { useInputValidations } from '@/composables/input-validations';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { usePermission } from '@/composables/use-permission';
import { useFormatNumber } from '@/composables/use-format-number';
import { useFeature } from '@/composables/use-feature';
import { useScenarioStatusFormatter } from '@/composables/use-scenario-status-formatter';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';

// stores
import { useContextStore } from '@/stores/context';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useAiStore } from '@/stores/ai';

export default {
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave(to, from, next);
  },

  setup() {
    const instance = getCurrentInstance();
    const { canEditCannGroupPage } = usePermission();
    const { aiPanelEnabled } = useFeature();
    const { formatScenarioProgress } = useScenarioStatusFormatter('cann-groups');

    // Shared helper: depth-first search for a node by key in the live tree.
    const findNodeByKey = (node, key) => {
      if (!node || !key) return null;
      if (node.key === key) return node;
      if (node.children) {
        let found = null;
        node.children.some(child => {
          found = findNodeByKey(child, key);
          return found !== null;
        });
        return found;
      }
      return null;
    };

    useAiAgentActions({
      allowScenarioWorkflowNavigation: true,
      getExclude: () =>
        instance.proxy.isEditingDisabled
          ? [
              'expand-cann-group',
              'delete-branch',
              'merge-cann-groups',
              'rename-cann-group',
              'save-cann-groups',
              'discard-cann-group-changes',
            ]
          : [],
      getSupplementary: () => ({
        'expand-cann-group': instance.proxy.buildExpandableNodesContext(),
      }),
      actions: {
        'expand-cann-group': {
          description:
            'Expand a cann group node by splitting it using an attribute. ' +
            'Use the nodeKey field exactly as shown in the expandable nodes context or leaf nodes list. ' +
            'Only use attributes listed as available (not blocked) for that specific node. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Splitting cann group',
          confirmationLabel: args => {
            const node = findNodeByKey(instance.proxy.treeData, args.nodeKey);
            const displayName = node?.name || node?.pathName || args.nodeKey;
            return `split cann group "${displayName}" by "${args.attributeName}"`;
          },
          parameters: {
            type: 'object',
            properties: {
              nodeKey: { type: 'string', description: 'The key of the cann group node to expand.' },
              attributeName: {
                type: 'string',
                description: 'The name of the attribute to split by.',
              },
            },
            required: ['nodeKey', 'attributeName'],
          },
          handler: async ({ nodeKey, attributeName }) => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit cann groups. Inform the user.';
            if (instance.proxy.isTreeLoading)
              return '[Tool] Action failed: the tree is currently loading. Ask the user to try again shortly.';
            if (!nodeKey || !attributeName)
              return '[Tool] Action failed: both nodeKey and attributeName are required. Ask the user to clarify.';
            const node = findNodeByKey(instance.proxy.treeData, nodeKey);
            if (!node)
              return `[Tool] Action failed: node with key "${nodeKey}" not found. Ask the user to clarify.`;
            if (node.children && node.children.length > 0)
              return `[Tool] Action failed: node "${nodeKey}" already has children. Use delete-branch first if you want to re-split it.`;
            const customAttributes = instance.proxy.selectedScenario?.customAttributes || [];
            const attribute = customAttributes.find(a => a.name === attributeName);
            if (!attribute)
              return `[Tool] Action failed: attribute "${attributeName}" not found. Ask the user to clarify.`;
            const validAttributes = instance.proxy.getAttributesToSelect(node.key);
            if (!validAttributes.find(a => a.id === attribute.id))
              return `[Tool] Action failed: attribute "${attributeName}" is already used by an ancestor level and cannot be reused here. Ask the user to choose a different attribute.`;
            const distinctValues = new Set(node.products.map(p => p[attribute.id])).size;
            if (distinctValues > 20)
              return `[Tool] Action blocked: expanding by "${attributeName}" would create ${distinctValues} branches — too many for a single split. Choose a different attribute.`;
            instance.proxy.modifyTree(attribute.id, node);
            const childCount = node.children?.length || 0;
            return `[Tool] Action succeeded: expanded node "${nodeKey}" by "${attributeName}" into ${childCount} groups.`;
          },
        },
        'delete-branch': {
          description:
            "Collapse a cann group node's entire sub-tree, making it a single leaf node again. Works at any level of the tree — not just the lowest split. " +
            'Use the nodeKey field exactly as shown in the tree structure. ' +
            'Only use a key of a node that currently has children (is not already a leaf). ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Removing branch',
          confirmationLabel: args => {
            const node = findNodeByKey(instance.proxy.treeData, args.nodeKey);
            const displayName = node?.name || node?.pathName || args.nodeKey;
            return `remove the branch at "${displayName}"`;
          },
          parameters: {
            type: 'object',
            properties: {
              nodeKey: { type: 'string', description: 'The key of the node to collapse.' },
            },
            required: ['nodeKey'],
          },
          handler: async ({ nodeKey }) => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit cann groups. Inform the user.';
            if (instance.proxy.isTreeLoading)
              return '[Tool] Action failed: the tree is currently loading. Ask the user to try again shortly.';
            const node = findNodeByKey(instance.proxy.treeData, nodeKey?.trim());
            if (!node)
              return `[Tool] Action failed: node with key "${nodeKey}" not found. Ask the user to clarify.`;
            if (!node.children || node.children.length === 0)
              return `[Tool] Action failed: node "${nodeKey}" is already a leaf — nothing to collapse.`;
            instance.proxy.deleteNode(node);
            return `[Tool] Action succeeded: collapsed branch at "${nodeKey}" — it is now a single cann group.`;
          },
        },
        'merge-cann-groups': {
          description:
            'Merge two sibling cann group leaf nodes together — combining all their attribute values into one group. ' +
            'Both nodes must be siblings (same parent node, same attribute level). ' +
            'Use the name field exactly as shown in the leaf nodes list. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Merging cann groups',
          confirmationLabel: args => `merge "${args.sourceName}" into "${args.targetName}"`,
          parameters: {
            type: 'object',
            properties: {
              sourceName: {
                type: 'string',
                description: 'The exact name of the source cann group (to be merged away).',
              },
              targetName: {
                type: 'string',
                description: 'The exact name of the target cann group (to merge into).',
              },
            },
            required: ['sourceName', 'targetName'],
          },
          handler: async ({ sourceName, targetName }) => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit cann groups. Inform the user.';
            if (instance.proxy.isTreeLoading)
              return '[Tool] Action failed: the tree is currently loading. Ask the user to try again shortly.';
            if (!sourceName || !targetName)
              return '[Tool] Action failed: both sourceName and targetName are required. Ask the user to clarify.';
            const source = instance.proxy.orderedLeafNodes.find(n => n.name === sourceName);
            if (!source)
              return `[Tool] Action failed: cann group "${sourceName}" not found in the leaf nodes list. Ask the user to clarify the name.`;
            const target = instance.proxy.orderedLeafNodes.find(n => n.name === targetName);
            if (!target)
              return `[Tool] Action failed: cann group "${targetName}" not found in the leaf nodes list. Ask the user to clarify the name.`;
            if (source === target)
              return '[Tool] Action failed: source and target are the same node. Ask the user to specify two different cann groups.';
            if (source.parentRef !== target.parentRef)
              return `[Tool] Action failed: "${sourceName}" and "${targetName}" are not siblings — they have different parent nodes. Can only merge nodes at the same level. Ask the user to clarify.`;
            if (source.attributeId !== target.attributeId)
              return `[Tool] Action failed: "${sourceName}" and "${targetName}" have different attribute types and cannot be merged.`;
            target.attributeValues = [...target.attributeValues, ...source.attributeValues];
            source.attributeValues = [];
            instance.proxy.mergeGroup({}, target);
            instance.proxy.mergeGroup({}, source);
            return `[Tool] Action succeeded: merged "${sourceName}" into the group now named "${target.name}". Use rename-cann-group to rename it if needed.`;
          },
        },
        'rename-cann-group': {
          description:
            'Rename a cann group leaf node. ' +
            'Use the current name exactly as shown in the leaf nodes list. The new name must be unique. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Renaming cann group',
          confirmationLabel: args => `rename "${args.currentName}" to "${args.newName}"`,
          parameters: {
            type: 'object',
            properties: {
              currentName: { type: 'string', description: 'Current exact name of the cann group.' },
              newName: { type: 'string', description: 'New unique name for the cann group.' },
            },
            required: ['currentName', 'newName'],
          },
          handler: async ({ currentName, newName }) => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit cann groups. Inform the user.';
            if (!currentName || !newName)
              return '[Tool] Action failed: both currentName and newName are required. Ask the user to clarify.';
            const leaf = instance.proxy.orderedLeafNodes.find(n => n.name === currentName);
            if (!leaf)
              return `[Tool] Action failed: cann group "${currentName}" not found. Ask the user to clarify the name.`;
            if (!leaf.pathName)
              return '[Tool] Action failed: this cann group cannot be renamed (single cann group mode — no attribute split applied yet).';
            const originalName = leaf.name;
            leaf.name = newName;
            const uniquenessResult = instance.proxy.isUniqueLeafNodeName(newName);
            if (typeof uniquenessResult === 'string') {
              leaf.name = originalName;
              return `[Tool] Action failed: the name "${newName}" is already in use by another cann group. Ask the user for a different name.`;
            }
            instance.proxy.revalidateForm();
            return `[Tool] Action succeeded: renamed "${currentName}" to "${newName}".`;
          },
        },
        'save-cann-groups': {
          description:
            'Save all current cann group changes to the database. ' +
            'IMPORTANT: Do NOT save automatically after individual operations like expand, delete, merge, or rename. ' +
            'Only call this tool when: (1) the user explicitly asks to save, ' +
            '(2) the user uses language like "go ahead", "apply", "save it", or "just do it", ' +
            'or (3) you have completed ALL steps of an explicitly user-approved multi-step plan and saving was part of the agreed plan. ' +
            'After any single structural change, tell the user what you did and ask if they want to continue or save. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Saving cann groups',
          confirmationLabel: 'save all cann group changes',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit cann groups. Inform the user.';
            if (instance.proxy.isTreeLoading)
              return '[Tool] Action failed: a save is already in progress. Ask the user to wait and try again.';
            if (instance.proxy.hasDataErrors)
              return '[Tool] Action failed: the form has validation errors (e.g. duplicate or empty cann group names). Ask the user to review before saving.';
            await instance.proxy.initCanngroupSave();
            if (instance.proxy.dependencyTreeModalOpen)
              return '[Tool] Action succeeded with required user input: the save detected that downstream work (such as switching analysis, CDTs, or clustering) will be affected by these cann group changes. A confirmation modal is now open on screen listing exactly what will be reset. The user needs to read it and either click to confirm the changes (accepting the resets) or cancel to go back. Let the user know what has happened and that they need to action the modal before you can continue. Once they have dealt with the modal, you can continue with any remaining steps.';
            return '[Tool] Action succeeded: cann groups saved.';
          },
        },
        'discard-cann-group-changes': {
          description:
            'Discard all unsaved cann group changes, reverting the tree to the last saved state. ' +
            'Only call this when the user explicitly asks to discard, undo, revert, or cancel their changes. ' +
            'Do not call this proactively. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Discarding changes',
          confirmationLabel: 'discard all unsaved cann group changes',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit cann groups. Inform the user.';
            if (!instance.proxy.hasDataChanges)
              return '[Tool] Action failed: there are no unsaved changes to discard. Inform the user.';
            instance.proxy.discard();
            if (instance.proxy.hasDataChanges)
              return '[Tool] Action failed: the discard did not fully revert the changes. Inform the user and suggest they refresh the page.';
            return '[Tool] Action succeeded: all unsaved cann group changes have been discarded.';
          },
        },
      },
    });

    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    } = useUnsavedDataWarning();

    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();

    const { formatNumber } = useFormatNumber();

    return {
      // permissions
      canEditCannGroupPage,
      aiPanelEnabled,
      formatScenarioProgress,
      // unsaved data warning
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      // validations
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      // formatters
      formatNumber,
    };
  },

  data() {
    return {
      treeData: null,
      dependencyTreeFeedback: {},
      dependencyTreeModalOpen: false,
      openedNodes: [],
      initialLeafNodes: [],
      rootKey: 'rootNode',
      orderedLeafNodes: [],
      customOrderedLeafNodes: [],
      rules: [this.isNotEmpty, this.isUniqueLeafNodeName],
      valid: true,
      isEditing: false,
      loadingCannGroups: false,
      savingCannGroups: false,
      showTreeStructureTrimmedWarning: false,
      isConfirmWithoutChildrenModalOpen: false,
    };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario', 'scenarioCannGroupsWithNotes']),
    ...mapState(useWorkpackagesStore, ['selectedWorkpackage']),
    ...mapState(useContextStore, ['getCannGroupsRecommendedSizes']),
    ...mapState(useAiStore, ['isSidePanelOpen']),

    isEditingDisabled() {
      return !this.canEditCannGroupPage;
    },

    treeDataArray() {
      if (
        // in case of a single cann group tree we want to alter the structure
        // to avoid tree expansion
        isUndefined(this.selectedScenario.customAttributes) &&
        size(this.treeData.children) === 1 &&
        isNull(this.treeData.children[0].attributeId)
      ) {
        return [{ ...this.treeData, children: [] }];
      }
      return [this.treeData];
    },

    isTreeLoading() {
      return this.loadingCannGroups || this.savingCannGroups;
    },

    treeStructureHasChanged() {
      const initialLeafNodes = this.initialLeafNodes.map(leaf => leaf.key);
      const orderedLeafNodes = this.orderedLeafNodes.map(leaf => leaf.key);

      // Structure has changed if different lengths
      if (size(initialLeafNodes) !== size(orderedLeafNodes)) return true;

      const leafNodeDiff = difference(orderedLeafNodes, initialLeafNodes);
      return !!size(leafNodeDiff);
    },

    leafNodeHasChanged() {
      const customOrderedLeafNodes = this.customOrderedLeafNodes.map(({ name, order }) => ({
        name,
        order,
      }));
      const orderedLeafNodes = this.orderedLeafNodes.map(({ name, order }) => ({
        name,
        order,
      }));
      return !isEqual(customOrderedLeafNodes, orderedLeafNodes);
    },

    formattedProducts() {
      // Products need to have their attributes as root level properties
      // For each one, get their attributes, format them and add any other required keys
      return this.$options.scenarioProducts.map(p => {
        const product = p.customAttributes.reduce((obj, attr) => set(obj, attr.id, attr.value), {});
        product.product = p.productKey;
        product.rateOfSale = p.rateOfSale;
        return product;
      });
    },

    totalSales() {
      return (this.treeData && this.treeData.salesAmount) || 0;
    },

    expandAll() {
      // collapse in case a single cann group has been saved but custom attributes are available
      if (!this.treeData) return false;
      return !(
        this.selectedScenario.customAttributes &&
        size(this.treeData.children) === 1 &&
        isNull(this.treeData.children[0].attributeId)
      );
    },

    showSidepanel() {
      return size(this.orderedLeafNodes);
    },

    hasDataErrors() {
      // required for <page-actions> component
      return !this.valid || !this.cannGroupMinimumSizeNotMet;
    },

    hasDataChanges() {
      // required for <page-actions> component
      return this.treeStructureHasChanged || this.leafNodeHasChanged;
    },

    cannGroupMinimumSizeNotMet() {
      return every(this.orderedLeafNodes, oln => get(oln, 'products.length', 0) > 1);
    },

    // note this logic is outside the hasDataChanges function,
    // because we don't want to warn the user if they leave the page without saving the initial single cann group
    hasSingleCannGroup() {
      // AOV3-1495 user should ALWAYS be allowed to proceed with single cann group,
      // including when they first land on the cann group page before editing it at all.
      // need get with non-empty default since computed can run before all data is loaded,
      // enabling button incorrectly for a few frames on page load.
      return isEmpty(get(this, 'treeData.children', [1]));
    },
  },

  watch: {
    scenarioCannGroupsWithNotes: {
      deep: true,
      handler(newCannGroups) {
        const scenarioCannGroupsWithNotesByKey = keyBy(newCannGroups, 'key');
        each(this.orderedLeafNodes, n => {
          n.totalNotes = get(scenarioCannGroupsWithNotesByKey[n.key], 'totalNotes', 0);
        });
      },
    },
  },

  async created() {
    this.loadingCannGroups = true;
    const params = { pick: ['productKey', 'rateOfSale', 'customAttributes', 'cannGroupId'] };
    // using $options insted of data prop as Vue doesn't seem to put watcher on it, and read/write is much faster
    this.$options.scenarioProducts = await this.fetchScenarioProducts({ params });
    // have to fetch cann groups with totalNotes count every time user switches tabs or refreshes the page
    const cannGroups = await this.fetchScenarioCannGroups();
    this.loadCannGroups(cannGroups);
    this.loadingCannGroups = false;

    if (this.selectedWorkpackage.templateId) {
      const templateScenario = await this.fetchScenarios({
        params: {
          where: { workpackageId: this.selectedWorkpackage.templateId },
          pick: ['cannGroups'],
        },
        options: { skipCommit: true },
      });
      const scenarioCannGroupsAttributes = this.getAttributeValuesUsedInCannGroups(
        this.selectedScenario.cannGroups
      );
      const templateCannGroupsAttributes = this.getAttributeValuesUsedInCannGroups(
        get(first(templateScenario), 'cannGroups', [])
      );
      this.showTreeStructureTrimmedWarning = !!size(
        difference(templateCannGroupsAttributes, scenarioCannGroupsAttributes)
      );
    }
    // Signal that page data is ready for the agent to act on.
    useAiStore().setContextReady(true);
  },

  mounted() {
    const aiStore = useAiStore();
    const contextDisplay = [this.selectedScenario?.name].filter(Boolean);
    aiStore.enableSidePanel({ section: 'cann-groups', contextDisplay });
    aiStore.setSidePanelContextFactory(() => this.buildInnerContext());
  },

  methods: {
    ...mapActions(useScenariosStore, [
      'updateScenarioCannGroups',
      'fetchScenarioCannGroups',
      'setSelectedScenario',
      'fetchScenarios',
      'setScenarioCannGroupsWithNotesMutation',
    ]),
    ...mapActions(useScenarioProductsStore, ['fetchScenarioProducts']),

    getAttributeValuesUsedInCannGroups(nodes = [], result = new Set()) {
      each(nodes, node => {
        const attrValues = node.attributeValues || [];
        result.add(`${node.attributeId}-${attrValues.join('-')}`);
        if (size(node.children)) this.getAttributeValuesUsedInCannGroups(node.children, result);
      });
      return [...result];
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    // function which defines cann groups node structure
    Node({
      attributeId,
      attributeValue,
      attributeValues,
      products,
      parentRef,
      children = [],
      name,
      salesAmount,
      path,
      pathName,
      key = this.createKey(),
    }) {
      return {
        key,
        attributeId,
        attributeValue,
        parentRef,
        attributeValues,
        products,
        children,
        name,
        salesAmount,
        path: path ? path.add(key) : null,
        pathName,
      };
    },

    splitGroup(mergedGroup, splitValue) {
      // Modify the previously merged group
      // Once you split, all children are invalidated
      mergedGroup.children = [];
      // Get the attributeId - this is the same for all groups within this level.
      const { attributeId } = mergedGroup;

      // Remove the value you're splitting on.
      const remainingAttributeValues = pull(mergedGroup.attributeValues, splitValue);
      mergedGroup.attributeValues = remainingAttributeValues;

      // Split the products based on whether they match the old or the new group
      const mergedNodeProducts = mergedGroup.products;
      const [remainingProducts, splitProducts] = partition(mergedNodeProducts, p => {
        return remainingAttributeValues.includes(p[attributeId]);
      });

      // the split group is a clone of the merged group in most respects - it's also shallow as its children are gone
      const splitGroup = { ...mergedGroup };
      splitGroup.key = this.createKey(); // Generate a new key - this will make diffing trickier.
      splitGroup.attributeValues = [splitValue]; // You split a single value so that's the only one in the new group.
      splitGroup.path = this.generatePath(splitGroup); // Assign split group the correct path

      // Update path names
      mergedGroup.pathName = this.generatePathName(mergedGroup);
      splitGroup.pathName = this.generatePathName(splitGroup);

      // Rename Groups
      mergedGroup.name = mergedGroup.pathName;
      splitGroup.name = splitGroup.pathName;

      // Assign products to their respective groups
      mergedGroup.products = remainingProducts;
      splitGroup.products = splitProducts;

      // Once subtree is assembled, recalculate sales
      this.setSalesForNode(mergedGroup);
      this.setSalesForNode(splitGroup);

      // Update current leaf nodes list using the merged group
      this.updateLeafNodes(mergedGroup);
      // Previous update did not included the new splitGroup. Add this seperately to leaf nodes
      this.addLeafNode(splitGroup);

      // Get reference to all groups for this attribute.
      const currentLevel = mergedGroup.parentRef.children;

      // Groups are ordered by sales - preserve this ordering.
      const index = sortedIndexBy(currentLevel, group => size(group.products));
      currentLevel.splice(index, 0, splitGroup);
    },

    mergeGroup(event, node) {
      // In a merge, this method is called for both the source and target node
      // Splits are handled by a separate mechanism - merges are the only operation to trigger this function
      // After a merge, children of both nodes are invalidated.
      node.children = [];
      const remainingAttributeValues = get(node, 'attributeValues', []); // There won't be attributeValues on the root node.
      const attributeId = get(node, 'attributeId'); // There won't be an attributeId on the root node.

      // Products need to be recalcaluated - only do this if you have some attributeValues to filter on
      // If it doesn't, do nothing - nodes with no values will be removed in a few lines
      if (size(remainingAttributeValues)) {
        const parentProducts = node.parentRef.products;
        const newProductsForNode = parentProducts.filter(p => {
          return remainingAttributeValues.includes(p[attributeId]);
        });
        node.products = newProductsForNode;
        node.pathName = this.generatePathName(node);
        node.name = node.pathName;
        this.setSalesForNode(node);
        this.updateLeafNodes(node);
      }
      // If the node now has no values, then it's been merged into another node
      // Remove that node by filtering the children of its parent (its own level)
      if (isEmpty(remainingAttributeValues)) {
        node.parentRef.children = node.parentRef.children.filter(c => c.key !== node.key);
        this.deleteLeafNodes(node.key);
        this.resetLeafNodeOrder();
      }
    },

    isUniqueLeafNodeName(name) {
      const newName = name.trim().toLowerCase();
      // Filter leaf nodes with the new name value.
      // If more than one leaf node is found, the name is not unique.
      const filteredLeaves = this.orderedLeafNodes.filter(
        leaf => leaf.name.toLowerCase() === newName
      );

      return (
        size(filteredLeaves) <= 1 ||
        this.$t('validationErrors.unique', [this.$t('cannGroupsSidepanel.name')])
      );
    },

    revalidateForm() {
      if (this.$refs.leafNodeForm) {
        this.$refs.leafNodeForm.validate();
      }
    },

    toggleEditing() {
      this.isEditing = !this.isEditing;
    },

    highlightInputText(event) {
      this.toggleEditing();
      event.target.select();
    },

    generateName(node) {
      if (node.attributeValues) {
        return node.parentRef.name
          ? `${node.parentRef.name}-${node.attributeValues.join('-')}`
          : node.attributeValues.join('-');
      }

      return '';
    },

    getList(node) {
      return get(node, 'attributeValues', []);
    },

    createKey() {
      return uuidv1();
    },

    hasNodes(list) {
      return size(list) && isArray(list);
    },

    setOpenNodes(nodes) {
      // This sets expanded false if you have a saved single cann group - otherwise it renders incorrectly
      this.openedNodes = !this.expandAll ? [] : nodes;
    },

    setSalesForNode(node) {
      if (node.children && node.children.length > 0) {
        node.salesAmount = sumBy(node.children, this.setSalesForNode);
      } else if (node.products) {
        node.salesAmount = sumBy(node.products, 'rateOfSale');
      }
      return node.salesAmount;
    },

    formatSalesRatio(node, tree) {
      return this.formatNumber({
        number: (100 * node.salesAmount) / tree.salesAmount || 0,
        format: 'integer',
      });
    },

    getValue(children) {
      return this.hasNodes(children) ? first(children).attributeId : null;
    },

    // gives distinct values between all attribute and selected ones
    getAttributesToSelect(key) {
      const attributes = differenceWith(
        this.selectedScenario.customAttributes,
        this.generateParents(key),
        (x, y) => x.id === y
      );
      return sortBy(attributes, item => item.name.toLowerCase());
    },

    modifyTree(selectedAttributeId, node) {
      this.openedNodes.push(node.key);
      this.addChildrenNode(selectedAttributeId, node);
      this.setSalesForNode(node);
      this.updateLeafNodes(node);
    },

    addChildrenNode(selectedAttributeId, node) {
      node.children = this.createChildrens({
        list: uniq(map(node.products, selectedAttributeId)),
        nodeValues: {
          parentRef: node,
          products: node.products,
          attributeId: selectedAttributeId,
        },
      });
    },

    // creates array of nodes based on attribute values
    createChildrens({ list, nodeValues }) {
      const { products, attributeId, parentRef } = nodeValues;
      return list.map(attributeValue => {
        const nodeProducts = products.filter(product => product[attributeId] === attributeValue);
        const isRoot = parentRef.key === this.rootKey;

        return this.Node({
          attributeId,
          attributeValue,
          parentRef,
          attributeValues: [attributeValue],
          products: nodeProducts,
          name: isRoot ? attributeValue : `${parentRef.pathName}-${attributeValue}`,
          // salesAmount is set in setSalesForNode
          path: new Set(parentRef.path),
          pathName: isRoot ? attributeValue : `${parentRef.pathName}-${attributeValue}`,
        });
      });
    },

    updateLeafNodes(node) {
      this.deleteLeafNodes(node.key);
      // New child nodes are leaf nodes
      const leafNodes = size(node.children) ? node.children : node;
      this.addLeafNode(leafNodes);
    },

    deleteLeafNodes(nodeKey) {
      // Delete all leaf nodes if parent node is root
      if (nodeKey === this.rootKey) {
        this.orderedLeafNodes = [];
      } else {
        // Remove every leaf node that is a child of the modified node
        this.orderedLeafNodes = this.orderedLeafNodes.filter(node => !node.path.has(nodeKey));
      }
    },

    addLeafNode(nodes) {
      if (nodes.key === this.rootKey) {
        nodes.name = this.$t('scenarioCannGroups.allProducts');
      }
      this.orderedLeafNodes = this.orderedLeafNodes.concat(nodes);
      this.resetLeafNodeOrder();
    },

    discard() {
      this.loadCannGroups(this.selectedScenario.cannGroups);
      this.revalidateForm();
    },

    deleteNode(node) {
      node.children = [];
      this.updateLeafNodes(node);
    },

    // iterates trough tree and gets path to node
    getPathToNode(key, node) {
      if (node.key === key) return [];
      if (isArray(node.children)) {
        for (let i = 0; i < node.children.length; i += 1) {
          const child = node.children[i];
          const childResult = this.getPathToNode(key, child);
          if (isArray(childResult))
            return [{ id: child.attributeId, value: child.attributeValue }].concat(childResult);
        }
      }
    },

    generateParents(nodeKey) {
      const path = this.getPathToNode(nodeKey, this.treeData);
      return path.map(({ id }) => id);
    },

    formatTreeOnSave(tree) {
      const { key, attributeId, attributeValues } = tree;
      const formattedTree = {
        key,
        attributeId,
        attributeValues,
        children: (tree.children || []).map(node => this.formatTreeOnSave(node)),
      };
      if (!size(tree.children)) {
        formattedTree.name = tree.name;
        formattedTree.order = isUndefined(tree.order)
          ? this.orderedLeafNodes.indexOf(tree)
          : tree.order;
      } else {
        formattedTree.name = tree.parentRef ? this.generateName(tree) : '';
      }
      return formattedTree;
    },

    resetLeafNodeOrder() {
      // Set the order value to the tree leaf nodes by their sales amounts
      this.orderedLeafNodes = orderBy(this.orderedLeafNodes, ['salesAmount', 'name'], ['desc']);
      // Custom order gets overridden
      this.customOrderedLeafNodes = cloneDeep(this.orderedLeafNodes);
    },

    setCustomLeafNodesOrder() {
      this.customOrderedLeafNodes = cloneDeep(this.orderedLeafNodes);
      this.customOrderedLeafNodes.forEach((leaf, idx) => {
        leaf.order = idx;
      });
    },

    setLeafNodeOrder() {
      if (this.leafNodeHasChanged) {
        // Make sure 'custom' leaf node order is up-to-date and contains new names
        this.setCustomLeafNodesOrder();

        const sortedLeaves = orderBy(this.orderedLeafNodes, ['key']);
        const sortedCustomLeaves = orderBy(this.customOrderedLeafNodes, ['key']);
        sortedLeaves.forEach((leaf, idx) => {
          leaf.order = sortedCustomLeaves[idx].order;
          leaf.name = sortedCustomLeaves[idx].name;
        });
      }
    },

    getProducts(node) {
      const parentProducts = !isNull(node.parentRef)
        ? node.parentRef.products
        : this.formattedProducts;

      const { attributeId, attributeValues } = node;

      // If it's a single cann group tree, values are null - return all products.
      if (isNull(attributeValues)) return parentProducts;

      const newProductsForNode = parentProducts.filter(p => {
        return attributeValues.includes(p[attributeId]);
      });
      return newProductsForNode;
    },

    generatePath(child) {
      const path =
        child.parentRef.key === this.rootKey || !child.parentRef.path
          ? [child.key]
          : [...child.parentRef.path, child.key];

      return new Set(path);
    },

    generatePathName(child) {
      if (child.attributeValues) {
        const attributeValuePath = map(
          child.attributeValues,
          v => v || this.$t('scenarioCannGroups.blankAttributeValue')
        ).join('-');
        return child.parentRef.key === this.rootKey || !child.parentRef.pathName
          ? attributeValuePath
          : `${child.parentRef.pathName}-${attributeValuePath}`;
      }
      return null;
    },

    formatTreeOnLoad(tree = [], parentRef = null) {
      tree.forEach(child => {
        this.openedNodes.push(child.key);
        child.parentRef = parentRef;
        child.products = this.getProducts(child);
        child.name = child.name || this.generateName(child);
        child.path = this.generatePath(child);
        child.pathName = this.generatePathName(child);

        if (size(child.children)) {
          child.children = this.formatTreeOnLoad(child.children, child);
        } else {
          this.orderedLeafNodes.push(child);
        }
      });
      return tree;
    },

    async initCanngroupSave() {
      if (!this.hasNodes(this.treeData.children)) {
        this.toggleIsConfirmWithoutChildrenModalOpen(true);
      } else {
        return this.saveCannGroups();
      }
    },

    async saveCannGroups(commit = false) {
      this.savingCannGroups = true;
      const tree = this.treeData;
      const isSingleCannGroupTree =
        size(tree.children) === 1 && isNull(tree.children[0].attributeId);
      if (isSingleCannGroupTree) return;

      // If we don't save, we need to keep all the current data like parentRef etc.
      // formatTreeOnSave does not mutate original presentational tree
      this.setLeafNodeOrder();

      // generated formatted tree data for save
      const treeDataFixed = size(tree.children)
        ? this.formatTreeOnSave(tree).children
        : this.getSingleCannGroupTree();

      const result = await this.updateScenarioCannGroups({
        id: this.selectedScenario._id,
        cannGroups: treeDataFixed,
        commitChanges: commit,
        cannGroupsChanged: this.treeStructureHasChanged,
      });

      this.savingCannGroups = false;

      if (result.data.needsFeedback) {
        this.dependencyTreeFeedback = result.data.output;
        this.dependencyTreeModalOpen = true;
      } else {
        this.loadingCannGroups = true;
        this.loadCannGroups(result.data.data.cannGroups);
        this.loadingCannGroups = false;
        return this.setSelectedScenario(result.data.data);
      }
    },

    getSingleCannGroupTree() {
      return [
        {
          key: this.createKey(),
          attributeId: null,
          attributeValues: null,
          order: 0,
          children: [],
          name: this.$t('scenarioCannGroups.allProducts'),
        },
      ];
    },

    loadCannGroups(data) {
      // Empty leafNodes before load
      this.orderedLeafNodes = [];

      const treePreFix = cloneDeep(data);
      const rootNode = this.Node({
        key: this.rootKey,
        children: [],
        attributeValues: [],
        products: this.formattedProducts,
      });
      const tree = this.formatTreeOnLoad(treePreFix, rootNode);

      rootNode.children = tree;
      // Calculate sales amount on loading tree so that we don't calculate it on render.
      // Also, adding sales amount will allow leaf nodes to be reordered when changes are made to the tree.
      this.setSalesForNode(rootNode);
      this.treeData = rootNode;

      this.orderedLeafNodes = orderBy(this.orderedLeafNodes, ['order']);
      if (this.scenarioCannGroupsWithNotes) {
        const scenarioCannGroupsWithNotesByKey = keyBy(this.scenarioCannGroupsWithNotes, 'key');
        each(this.orderedLeafNodes, n => {
          n.totalNotes = get(scenarioCannGroupsWithNotesByKey[n.key], 'totalNotes', 0);
        });
      }
      this.customOrderedLeafNodes = cloneDeep(this.orderedLeafNodes);
      // Store the cann groups available on load
      this.initialLeafNodes = cloneDeep(this.orderedLeafNodes);
      this.setScenarioCannGroupsWithNotesMutation({ cannGroups: this.initialLeafNodes });
    },

    displayCannGroupHighlighting(productsNo, nodeChildren) {
      return (
        !(
          this.getCannGroupsRecommendedSizes.min <= productsNo &&
          this.getCannGroupsRecommendedSizes.max >= productsNo
        ) && !this.hasNodes(nodeChildren)
      );
    },

    toggleIsConfirmWithoutChildrenModalOpen(isOpen) {
      this.isConfirmWithoutChildrenModalOpen = isOpen;
    },

    buildInnerContext() {
      const totalCannGroups = this.orderedLeafNodes?.length || 0;
      const totalProducts = this.treeData?.products?.length || 0;

      const sizeIssues =
        this.orderedLeafNodes?.filter(node =>
          this.displayCannGroupHighlighting(node.products.length, node.children)
        ).length || 0;

      const context = {
        section: 'cann-groups',
        contextDisplay: [this.selectedScenario?.name, `${totalCannGroups} Cann Groups`].filter(
          Boolean
        ),
        context: {
          scenarioId: this.selectedScenario?._id,
          scenarioName: this.selectedScenario?.name || 'No scenario selected',
          totalCannGroups,
          totalProducts,
          scenarioProgress: this.formatScenarioProgress,
          groupsOutsideRecommendedSize: sizeIssues,
          hasSingleCannGroup: this.hasSingleCannGroup || false,
          isTreeLoading: this.isTreeLoading,
          availableAttributes: this.selectedScenario?.customAttributes?.map(a => a.name) || [],
          treeStructure: this.formatTreeStructure(this.treeData),
          leafNodes: (() => {
            const leaves = this.orderedLeafNodes || [];
            if (leaves.length > 30)
              return `Too many leaf nodes to list (${leaves.length} total). Use the tree structure to navigate, or ask the user to name the specific group.`;
            return leaves.map(n => ({
              key: n.key,
              name: n.name,
              pathName: n.pathName || null,
              productCount: n.products?.length || 0,
            }));
          })(),
        },
      };

      if (context.context.scenarioId) {
        return context.context;
      }
      return {};
    },

    buildExpandableNodesContext() {
      if (!this.treeData || !this.selectedScenario?.customAttributes?.length)
        return 'No tree data available.';

      const MAX_ATTRIBUTE_VALUES = 30;
      const MAX_NODES_IN_CONTEXT = 30;

      // Collect all leaf nodes from the live tree (nodes with no children that have a pathName,
      // or the unsplit "All Products" leaf which has attributeId == null (null or undefined —
      // covers both the saved single-group state and the brand new never-saved state).
      const collectLeafNodes = node => {
        if (!node) return [];
        if (!node.children || node.children.length === 0) {
          return node.pathName != null || node.attributeId == null ? [node] : [];
        }
        return node.children.flatMap(child => collectLeafNodes(child));
      };

      const leafNodes = collectLeafNodes(this.treeData);
      const totalLeafNodes = leafNodes.length;
      const displayNodes = leafNodes.slice(0, MAX_NODES_IN_CONTEXT);

      if (!displayNodes.length) return 'No expandable nodes found.';

      const lines = displayNodes.map(node => {
        const validAttributes = this.getAttributesToSelect(node.key);
        const available = [];
        const blocked = [];

        validAttributes.forEach(attr => {
          const distinctCount = new Set(node.products.map(p => p[attr.id])).size;
          if (distinctCount >= MAX_ATTRIBUTE_VALUES) {
            blocked.push(`${attr.name} (${distinctCount} values — too many)`);
          } else {
            available.push(`${attr.name} (${distinctCount} values)`);
          }
        });

        const availableStr = available.length ? available.join(', ') : 'none';
        const blockedStr = blocked.length ? ` — blocked: ${blocked.join(', ')}` : '';
        const displayName = node.name || node.pathName || this.$t('scenarioCannGroups.allProducts');
        return `key:${node.key} "${displayName}": available: [${availableStr}]${blockedStr}`;
      });

      if (totalLeafNodes > MAX_NODES_IN_CONTEXT) {
        lines.push(
          `...and ${totalLeafNodes - MAX_NODES_IN_CONTEXT} more nodes not shown. Ask the user to specify which node to work on.`
        );
      }

      return lines.join('\n');
    },

    formatTreeStructure(node) {
      if (!node) return null;

      const result = {
        productCount: node.products?.length || 0,
      };

      if (node.children && node.children.length > 0) {
        const attributeId = node.children[0]?.attributeId;
        const attributeName = attributeId
          ? this.selectedScenario?.customAttributes?.find(a => a.id === attributeId)?.name ||
            attributeId
          : 'Unknown';

        result.splitByAttribute = attributeName;

        const displayChildren = node.children.slice(0, 5);
        result.branches = displayChildren.map(child => {
          const isLeaf = !child.children || child.children.length === 0;
          const branch = {
            key: child.key,
            pathName: child.pathName || null,
            attributeValues: child.attributeValues?.join(', ') || 'Unknown',
            productCount: child.products?.length || 0,
            isLeaf,
          };
          if (!isLeaf) {
            branch.children = this.formatTreeStructure(child);
          }
          return branch;
        });

        if (node.children.length > 5) {
          result.additionalBranchCount = node.children.length - 5;
        }
      }

      return result;
    },
  },
};
</script>

<style lang="scss">
@import '@style/base/_variables.scss';

$attribute-width: 18.6rem;

.cann-groups-container {
  font-size: 12px;
  max-width: none !important;
  color: $assortment-text-colour;
  height: 100%;

  .header {
    border-bottom: 1px solid $assortment-divider-colour;

    &__title {
      white-space: nowrap;
    }
  }

  .container-block {
    overflow: hidden;
    position: relative;
    height: $tab-panel-height;

    .drag-disabled {
      opacity: 0.5;
    }

    .tree {
      display: flex;
      overflow: auto;
      height: 100%;

      .title-container {
        display: flex;
        flex-direction: column;
        padding: 5px 3px 0px 10px;
      }

      .treeview-container {
        padding: 16px $assortment-sidepanel-toggle-height 0 0;
        width: auto;
        max-width: none;
      }

      .treeview-select {
        align-self: center;
        min-width: $attribute-width;

        &.attribute-not-split-select {
          opacity: 0.5;
        }

        .v-field__input {
          padding-left: 5px;
          height: 28px;
          font-weight: bold;

          span {
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
          }
        }

        .v-input__slot {
          box-shadow: none !important;
          padding: 0 !important;
        }

        .v-icon {
          padding-right: 0 !important;
        }

        .v-label {
          position: unset !important;
          padding-left: 5px;
          font-size: 1.2rem;
        }

        .v-select__selection.v-select__selection--comma {
          padding-left: 5px !important;
        }
      }

      .v-treeview-node__label {
        display: flex;
      }

      .node-chips-group {
        position: relative;
      }

      .node-group-line {
        width: 5px;
        height: calc(100% - 4px);
        left: -9px;
        top: 2px;
        border-top: 1px solid $assortment-tree-line-colour;
        border-left: 1px solid $assortment-tree-line-colour;
        border-bottom: 1px solid $assortment-tree-line-colour;
        position: absolute;
      }

      .v-treeview-node__root {
        i.v-treeview-node__toggle {
          margin-bottom: 0px !important;
          visibility: hidden;
        }
      }

      .v-treeview-node__children {
        border-left: 1px solid $assortment-tree-line-colour;
        padding-left: 100px;
        .v-treeview-node__root {
          border-left: 1px solid $assortment-tree-line-colour;
          i.v-treeview-node__toggle {
            margin-bottom: 5px !important;
            visibility: hidden;
          }
        }

        /* Overrides to stop trailing lines */
        .v-treeview-node:last-child > .v-treeview-node__children {
          border-left: 1px solid white;
        }
        .v-treeview-node:last-child
          > .v-treeview-node__root
          > .v-treeview-node__content
          > .v-treeview-node__label {
          border-left: 1px solid white;
        }
      }

      .attribute-products {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f4f4f4;
        height: 50px;
        min-width: 50px;
        height: 28px;
        border-radius: 3px;
        font-size: 1.2rem;
        padding-right: 3px;
        margin-left: 0px;
        margin-right: 5px;
      }

      .attribute-products-highlighted {
        border: 1px solid $assortment-negative-action-colour;
      }

      .attribute-value {
        background: $assortment-attribute-colour;
        width: $attribute-width;
        height: 28px;
        border-radius: 3px;
        padding-left: 10px;
        padding-right: 3px;
        margin-right: 10px;

        p {
          font-weight: bold;
          font-size: 1.2rem;
          margin-bottom: 0px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1;
        }

        &--all-products {
          margin-top: 20px;
          margin-right: 0;
        }

        &.hidden {
          display: none !important;
        }
      }

      .v-treeview {
        .v-treeview-node__label {
          .v-btn {
            margin-top: 2px;
            margin-left: 2px;
          }
        }
      }

      /* Specifically remove the first border, to stop the whole treeview having a left border */
      .v-treeview > .v-treeview-node > .v-treeview-node__children {
        border: none;
      }

      .v-treeview
        > .v-treeview-node
        > .v-treeview-node__root
        > .v-treeview-node__content
        > .v-treeview-node__label {
        display: none;
      }

      .v-treeview > .v-treeview-node > .v-treeview-node__root > .v-treeview-node__toggle {
        display: none;
      }
    }
  }

  .page-actions-container {
    border-top: 1px solid $assortment-panel-border-divider-colour;
  }

  .drag-handle {
    cursor: move;
  }

  .list-group {
    position: relative;

    &__item {
      align-items: center;
      background-color: $assortment-cann-group-row-bg-white;
      border: 1px solid $assortment-cann-group-row-bg-white;
      color: $assortment-cann-group-row-colour;
      display: flex;
      font-size: 1.2rem;
      line-height: 1.5rem;
      padding: 4px 10px 4px 2px;

      &:nth-child(odd) {
        background-color: $assortment-cann-group-row-bg-blue;
        border-color: $assortment-cann-group-row-bg-blue;

        .v-input:not(.v-input--is-focused),
        .v-input--is-disabled {
          .v-input__slot {
            background-color: $assortment-cann-group-row-bg-blue;
          }
        }
      }
    }

    &__input {
      .v-text-field__details,
      .v-messages {
        min-height: 0;
      }

      .v-messages.error--text {
        color: $assortment-negative-action-colour !important;
        min-height: 14px;
      }

      &.v-input:not(.v-input--is-focused),
      &.v-input--is-disabled {
        .v-input__slot {
          background-color: $assortment-cann-group-row-bg-white;

          &:before,
          &:after {
            border-color: transparent !important;
            border-image: none !important;
          }

          .v-text-field__slot input {
            color: $assortment-cann-group-row-colour;
          }
        }
      }

      &.v-input.error--text {
        .v-input__slot {
          &:before,
          &:after {
            border-color: $assortment-negative-action-colour !important;
          }
        }
      }

      &.v-input--is-focused,
      &.error--text {
        .v-input__slot {
          background-color: $assortment-control-secondary-bg-colour !important;
        }
      }
    }

    .draggable__icon {
      cursor: move;
      margin: 3px;
    }
  }

  .v-list {
    padding: 0;
  }
}
</style>
